import { Column, formatDateStrWithTz } from "@coherehealth/common";
import { ServiceCase, ReviewOutcome } from "@coherehealth/qm-api";
import { UserCtx } from "./fieldRegistry";
import { getReviewCaseOutcomeMapping } from "../../util/TableUtil";

/**
 * Reference: the BE sort allow-list for the advancedSearch endpoint.
 * Sort eligibility at the column level is controlled via `unSortable: true` on each Column definition.
 * This set is documentation-only: it is never imported at runtime. Its purpose is to document the
 * BE allow-list and to be cross-checked by the test "every COLUMN_SORT_KEYS value is a member of
 * SORTABLE_COLUMNS", which guards against accidentally using a sort key the BE does not support.
 * Keep it in sync with the column definitions manually.
 */
// BE allow-list (AdvancedSearchService.SORTABLE_FIELDS on develop):
// dateCreated, dateCreatedAsLong, lastUpdated, lastUpdatedAsLong, dueDate, dueDateAsLong,
// caseCompletedTimeStampAsLong, caseNumber, caseStatus, casePriority, caseType, isEscalated.
// healthPlan is NOT in the list (sorts silently fall back to dateCreated on the BE).
export const SORTABLE_COLUMNS = new Set(["caseNumber", "dateCreated", "caseCompletedTimeStampAsLong", "caseType"]);

/** Maps UI column names to their BE sort field equivalents where they differ. */
export const COLUMN_SORT_KEYS: Record<string, string> = {
  workRequested: "dateCreated",
  workCompleted: "caseCompletedTimeStampAsLong",
};

const DATE_TIME_FORMAT = "MM/dd/yyyy - hh:mma"; // matches existing QM table format (TableUtil.tsx)
const COHERE_TIMEZONE = "America/New_York";

function capitalizeAndRemoveUnderscore(str: string): string {
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function formatDateTime(dateStr?: string): string {
  if (!dateStr) {
    return "--";
  }
  const formatted = formatDateStrWithTz(dateStr, COHERE_TIMEZONE, DATE_TIME_FORMAT);
  return formatted || "--";
}

function renderOutcome(sc: ServiceCase): string {
  const reviewOutcome = sc.outcome?.reviewOutcome;
  const faxOutcome = sc.outcome?.outcome;
  if (reviewOutcome) {
    const mapping = getReviewCaseOutcomeMapping(sc.healthPlan);
    return mapping?.[reviewOutcome as ReviewOutcome] || String(reviewOutcome);
  }
  if (faxOutcome) {
    return capitalizeAndRemoveUnderscore(faxOutcome);
  }
  return "--";
}

const ALL_COLUMNS: Column<ServiceCase>[] = [
  {
    name: "caseNumber",
    label: "Case ID",
    width: "10%",
    themedPaddingRight: 2,
    value: (sc) => sc.caseNumber || "--",
  },
  {
    name: "trackingNumber",
    label: "Tracking #",
    width: "10%",
    themedPaddingRight: 2,
    unSortable: true,
    value: (sc) => sc.serviceRequest?.cohereId || "--",
  },
  {
    name: "healthPlan",
    label: "Health plan",
    width: "10%",
    themedPaddingRight: 2,
    unSortable: true, // healthPlan is not in the BE advancedSearch SORTABLE_FIELDS allow-list
    value: (sc) => sc.healthPlan || "--",
  },
  {
    name: "caseType",
    label: "Case type",
    width: "9%",
    themedPaddingRight: 2,
    value: (sc) => (sc.caseType ? capitalizeAndRemoveUnderscore(sc.caseType) : "--"),
  },
  {
    name: "outcome",
    label: "Outcome",
    width: "10%",
    themedPaddingRight: 2,
    unSortable: true,
    value: renderOutcome,
  },
  {
    name: "workRequested",
    label: "Work requested",
    width: "15%",
    themedPaddingRight: 2,
    value: (sc) => formatDateTime(sc.dateCreated),
  },
  {
    name: "workCompleted",
    label: "Work completed",
    width: "15%",
    themedPaddingRight: 2,
    // Display uses outcome.dateCompleted (nested timestamp written at case close).
    // Sort key maps to caseCompletedTimeStampAsLong via COLUMN_SORT_KEYS. The displayed value and
    // the sort field can differ; the BE sort field is canonical.
    value: (sc) => formatDateTime(sc.outcome?.dateCompleted),
  },
  {
    name: "assignee",
    label: "Assignee",
    width: "15%",
    themedPaddingRight: 2,
    unSortable: true,
    allowTextWrap: true,
    value: (sc) => sc.assignee?.name || "Unassigned",
  },
];

export function getSearchColumns(
  userCtx: UserCtx,
  healthPlanLabels: Record<string, string> = {}
): Column<ServiceCase>[] {
  return ALL_COLUMNS.filter((col) => {
    if (col.name === "healthPlan" && !userCtx.hasCrossClientAccess) {
      return false;
    }
    return true;
  }).map((col) => {
    if (col.name !== "healthPlan") {
      return col;
    }
    return {
      ...col,
      value: (sc) => (sc.healthPlan ? healthPlanLabels[sc.healthPlan] || sc.healthPlan : "--"),
    };
  });
}
